import { writable as svelteWritable } from 'svelte/store';

/*
  this writable function replaces the svelte native writable. It may be used in a very similar way as
  the native one but provide reactive local/session storage and retrieval.
  To activate the storage, the method useLocalStorage must be called once (onMount). When refreshing the page, the store will
  populate itself automatically and when set, the localStorage will be updated.
  A reset() method can be used to set the value back to the starting value.
  example : const myStoredArray = writable( { myArray:[] } )
             and in the imported file : myStoredArray.useLocalStorage();
                                        $myStoredArray = [1,2,3];
                                        $myStoredArray.reset();
                                        $myStoredArray === [] (=> true)
*/

export const writable = (obj, namespace = 'default', useSessionStorage = false) => {
	const [key, startValue] = Object.entries(obj)[0];
	const { subscribe, set } = svelteWritable(startValue);
	return {
		subscribe,
		set,
		useLocalStorage: () => {
			if (sessionStorage && localStorage) {
				const storage = useSessionStorage ? sessionStorage : localStorage;
				const json = storage.getItem(namespace);
				if (json)
					try {
						const allStores = JSON.parse(json);
						if (allStores !== null && key in allStores) {
							const value = allStores[key];
							set(value);
						}
					} catch (error) {
						// Reset the storage if the JSON is corrupted
						console.error('error while restoring store value from localstorage JSON', error);
						localStorage.clear();
						window.location.reload();
					}
				subscribe((current) => {
					current = current === undefined ? null : current;
					const allStores = JSON.parse(storage.getItem(namespace)) || {};
					allStores[key] = current;
					storage.setItem(namespace, JSON.stringify(allStores).replace(/\\'/g, "\\'"));
				});
			} else console.log("WARNING: localStorage/sessionStorage isn't available from server side");
		},
		reset: () => {
			set(startValue);
		}
	};
};

const escapeJSONchars = (s) => s.replace(/\\'/g, "\\'");

/*
  urlToProperties should be an array of {url: [storeObjectKeys]} objects in the order of the progression
  for example :
    getUrlForStore ( "user",
                     [ {"/step1": ["firstname", "lastname"]}, {"/step2": ["phone", "address"] }]
                    )
    will return :
      "/" if either of the user.firstname or user.lastname is undefined
      "/step1" if both the user.firstname and user.lastname are defined but if either phone or address are missing
      "/step2" only if user.firstname, user.lastname, user.phone and user.address are defined
*/
export const getUrlForStore = (storeObject, urlsToProperties) => {
	let urlToReturn = '/';
	for (let item of urlsToProperties) {
		if (Object.values(item)[0].every((property) => !!storeObject[property]))
			urlToReturn = Object.keys(item)[0];
		else break;
	}
	return urlToReturn;
};
